import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { favoritesService, bookmarksService } from '../services/userActions';
import { Property } from '../types/property';

interface UserActionsContextType {
  favorites: Property[];
  bookmarks: Property[];
  isFavorited: (propertyId: string) => boolean;
  isBookmarked: (propertyId: string) => boolean;
  toggleFavorite: (propertyId: string) => Promise<void>;
  toggleBookmark: (propertyId: string) => Promise<void>;
  loading: boolean;
  refreshData: () => Promise<void>;
}

const UserActionsContext = createContext<UserActionsContextType | undefined>(undefined);

// Every PropertyCard/BookmarkButton/etc. on a page used to call a
// per-component useUserActions() hook independently — each instance had its
// own favoriteStatus/bookmarkStatus state and fired its own GET /favorites +
// GET /bookmarks on mount. Toggling on one card never reflected on another
// until that other card's own fetch happened to resolve. This mirrors
// VendorContext's already-working single-shared-Provider pattern instead.
export const UserActionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [favorites, setFavorites] = useState<Property[]>([]);
  const [bookmarks, setBookmarks] = useState<Property[]>([]);
  const [favoriteStatus, setFavoriteStatus] = useState<Record<string, boolean>>({});
  const [bookmarkStatus, setBookmarkStatus] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);

  const loadUserData = useCallback(async () => {
    if (!isAuthenticated || !user) return;

    setLoading(true);
    try {
      const [favoritesData, bookmarksData] = await Promise.all([
        favoritesService.getFavorites(),
        bookmarksService.getBookmarks(),
      ]);

      setFavorites(favoritesData.data.map((fav: any) => fav.Property));
      setBookmarks(bookmarksData.data.map((bookmark: any) => bookmark.Property));

      const newFavoriteStatus: Record<string, boolean> = {};
      const newBookmarkStatus: Record<string, boolean> = {};

      favoritesData.data.forEach((fav: any) => {
        if (fav.Property) newFavoriteStatus[fav.Property.id] = true;
      });

      bookmarksData.data.forEach((bookmark: any) => {
        if (bookmark.Property) newBookmarkStatus[bookmark.Property.id] = true;
      });

      setFavoriteStatus(newFavoriteStatus);
      setBookmarkStatus(newBookmarkStatus);
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (isAuthenticated && user?.id) {
      loadUserData();
    } else {
      setFavorites([]);
      setBookmarks([]);
      setFavoriteStatus({});
      setBookmarkStatus({});
    }
  }, [isAuthenticated, user?.id, loadUserData]);

  const toggleFavorite = useCallback(async (propertyId: string) => {
    if (!isAuthenticated || !user) {
      setFavoriteStatus(prev => ({ ...prev, [propertyId]: !prev[propertyId] }));
      const shouldLogin = window.confirm('Please log in to save your favorites permanently. Would you like to log in now?');
      if (shouldLogin) {
        window.location.href = '/login';
      }
      return;
    }

    try {
      const isCurrentlyFavorited = favoriteStatus[propertyId];

      if (isCurrentlyFavorited) {
        await favoritesService.removeFavorite(propertyId);
        setFavorites(current => current.filter(p => p.id !== propertyId));
        setFavoriteStatus(prev => ({ ...prev, [propertyId]: false }));
      } else {
        const newFavorite = await favoritesService.addFavorite(propertyId);
        setFavorites(current => [...current, newFavorite.data.Property]);
        setFavoriteStatus(prev => ({ ...prev, [propertyId]: true }));
      }
    } catch (error) {
      // No optimistic update happens above (unlike the logged-out branch) —
      // favoriteStatus is only set after the backend call succeeds. Flipping
      // it here on failure was backwards: it marked a *failed* save as
      // favorited, so the heart showed as filled while nothing was actually
      // saved — exactly the "looked like it worked, then vanished on next
      // load" symptom this masked. Just surface the failure instead.
      console.error('Error toggling favorite:', error);
      window.alert('Could not save your favorite. Please try again.');
    }
  }, [isAuthenticated, user, favoriteStatus]);

  const toggleBookmark = useCallback(async (propertyId: string) => {
    if (!isAuthenticated || !user) {
      setBookmarkStatus(prev => ({ ...prev, [propertyId]: !prev[propertyId] }));
      const shouldLogin = window.confirm('Please log in to save your bookmarks permanently. Would you like to log in now?');
      if (shouldLogin) {
        window.location.href = '/login';
      }
      return;
    }

    try {
      const isCurrentlyBookmarked = bookmarkStatus[propertyId];

      if (isCurrentlyBookmarked) {
        await bookmarksService.removeBookmark(propertyId);
        setBookmarks(current => current.filter(p => p.id !== propertyId));
        setBookmarkStatus(prev => ({ ...prev, [propertyId]: false }));
      } else {
        const newBookmark = await bookmarksService.addBookmark(propertyId);
        setBookmarks(current => [...current, newBookmark.data.Property]);
        setBookmarkStatus(prev => ({ ...prev, [propertyId]: true }));
      }
    } catch (error) {
      // Same reasoning as toggleFavorite's catch block above — there's no
      // optimistic update here to revert, so flipping bookmarkStatus on
      // failure incorrectly displayed a failed save as successful.
      console.error('Error toggling bookmark:', error);
      window.alert('Could not save your bookmark. Please try again.');
    }
  }, [isAuthenticated, user, bookmarkStatus]);

  const isFavorited = useCallback((propertyId: string) => {
    return favoriteStatus[propertyId] || false;
  }, [favoriteStatus]);

  const isBookmarked = useCallback((propertyId: string) => {
    return bookmarkStatus[propertyId] || false;
  }, [bookmarkStatus]);

  return (
    <UserActionsContext.Provider
      value={{
        favorites,
        bookmarks,
        isFavorited,
        isBookmarked,
        toggleFavorite,
        toggleBookmark,
        loading,
        refreshData: loadUserData,
      }}
    >
      {children}
    </UserActionsContext.Provider>
  );
};

export const useUserActions = (): UserActionsContextType => {
  const context = useContext(UserActionsContext);
  if (context === undefined) {
    throw new Error('useUserActions must be used within a UserActionsProvider');
  }
  return context;
};
